import { isAnyOf } from '@reduxjs/toolkit';
import { isNotDefined } from '@sgme/fp';

import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import { cashApi } from '@/store/cash/api/index.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { cashHeartbeatTimeout, cashSlice } from '@/store/cash/slice.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

const heartbeatListenersByRfsId: Record<string, (rfsId: string) => void> = {};

export const registerCancelCashRfsListeners = (startAppListening: StartAppListening) => {
  startAppListening({
    matcher: cashApi.endpoints.requestCashRfs.matchFulfilled,
    effect: async (action, api) => {
      const { rfsId, tileId } = action.meta.arg.originalArgs;
      const signalRConnector = getSignalRConnector();

      function onHeartbeatTimeout(id: string) {
        if (id !== rfsId) {
          return;
        }

        api.dispatch(cashHeartbeatTimeout({ tileId }));
        api.dispatch(cashApi.endpoints.cancelCashRfs.initiate({ tileId, rfsId }));
      }

      heartbeatListenersByRfsId[rfsId] = onHeartbeatTimeout;
      signalRConnector.onHeartbeatTimeout(rfsId, onHeartbeatTimeout);
    },
  });

  startAppListening({
    matcher: cashApi.endpoints.executeCashRfs.matchPending,
    effect: async (action) => {
      const { rfsId } = action.meta.arg.originalArgs;
      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });

  startAppListening({
    matcher: cashApi.endpoints.cancelCashRfs.matchPending,
    effect: async (action) => {
      const { rfsId } = action.meta.arg.originalArgs;

      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });

  /**
   * This listener is triggered when trade capture is called.
   */
  startAppListening({
    matcher: cashApi.endpoints.checkCashByTradeCapture.matchFulfilled,
    effect: async (action, api) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const tradeCapture = action.payload;

      if (isNotDefined(tradeCapture) || !tradeCapture[tileId]?.changedFields.isPriceObsolete) {
        return;
      }

      // read the state as it was before this trade-capture response was applied: the reducer for
      // a transition to FxSpot drops `properties.rfs` from the new tile (unlike Forward/NDF, which
      // preserve it), so by the time `api.getState()` would resolve, the RFS to cancel is already gone
      cancelCashRfs(tileId, api.getOriginalState(), api.dispatch);
    },
  });

  /**
   * This listener is triggered when:
   * - the workspace client is changed
   * - the tile product is changed
   * - the tile is closed
   */
  startAppListening({
    // actionCreator: cashSlice.actions.cashClientIdUpdated,
    matcher: isAnyOf(
      cashSlice.actions.cashClientIdUpdated.match,
      tilesSlice.actions.updateTileProductType.match,
      tilesSlice.actions.removeTile.match,
    ),
    effect: async (action, api) => {
      const state = api.getOriginalState();

      const tileId =
        cashSlice.actions.cashClientIdUpdated.match(action) || tilesSlice.actions.updateTileProductType.match(action)
          ? action.payload.tileId
          : tilesSlice.actions.removeTile.match(action)
            ? action.payload
            : undefined;

      if (isNotDefined(tileId)) {
        return;
      }

      const tile = selectCashTile(state, tileId);

      if (isNotDefined(tile?.properties.rfs)) {
        return;
      }

      cancelCashRfs(tileId, state, api.dispatch);
    },
  });
};

function cancelCashRfs(tileId: string, state: AppState, dispatch: AppDispatch) {
  const tile = selectCashTile(state, tileId);

  if (isNotDefined(tile?.properties.rfs)) {
    return;
  }

  dispatch(
    cashApi.endpoints.cancelCashRfs.initiate({
      rfsId: tile.properties.rfs.rfsId,
      tileId,
    }),
  );
}
